import React, { Suspense, useCallback, useContext, useEffect, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import { useNavigate, useParams } from "react-router";
import axios from "axios";
import { Apis } from "../util/Apis";
import anforaSolbet from "../assets/gifs/anfora-solbet-infinite.gif";
import ContainerBackgroundWrapper from "../components/Backgrounds";
import tiempoAnimacion from "../assets/dataSala/tiempoAnimacion.json";
import { Canvas } from "@react-three/fiber";
import { Stage } from "@react-three/drei";
import AnforaAnimate from "../components/AnforaAnimate";
import AnforaFallback from "../components/AnforaFallback";
import ErrorBoundary from "../components/ErrorBoundary";
import { WEBGL_HARDWARE_ONLY, hasHardwareWebGL } from "../util/webgl";
import { AnimatePresence, motion } from "motion/react";
import { SiteContext } from "../context/SiteContext";
import { useCustomCountdown } from "../hooks/useCustomCountdown";
import Button from "../components/atoms/Buttons";
import beepSound from "../../src/assets/sounds/beep.wav";
import { RaffleStage } from "./PlaySorteo";
import RaffleWinnerOverlay from "../components/virtualRaffle/RaffleWinnerOverlay";
import RaffleWinnerFallback from "../components/virtualRaffle/RaffleWinnerFallback";
import RaffleEmptyContent from "../components/virtualRaffle/RaffleEmptyContent";
import { hasRaffleWinner } from "../components/virtualRaffle/raffleResult";

// El resultado entra cuando el cupón ya está saliendo de la ánfora (su salida dura los últimos 3 s).
const REVEAL_LEAD_MS = 1500;
const FADE_MS = 1100;
const ANFORA_OUT_MS = 500;   // la ánfora (y su título) se desvanece primero; recién después entra el cupón

const resultIn = keyframes`
  from { opacity: 0; transform: scale(1.03); }
  to { opacity: 1; transform: none; }
`;
const ResultStage = styled(RaffleStage)`
  position: absolute;
  inset: 0;
  animation: ${resultIn} ${FADE_MS}ms cubic-bezier(0.2, 0.7, 0.2, 1) ${ANFORA_OUT_MS - 150}ms both;
  @media (prefers-reduced-motion: reduce) { animation: none; }
`;

// Mismo dorado con relieve que el título de FELICIDADES y del Hot Seat.
const GoldHeading = styled.h1`
  margin: 0.4rem 0 0;
  font: 800 clamp(28px, 5vw, 68px) / 1.1 Arial, sans-serif;
  letter-spacing: -0.04em;
  color: #ffe9a4;
  text-shadow: 0 1px #ffd875, 0 3px #b8791a, 0 5px #72300d, 0 8px 14px #280000;
`;

const CountDownContainer = styled(motion.div)`
  color: white;
  display: ${(props) => (props.$show ? "flex" : "none")};
  align-items: center;
  justify-content: center;
  flex-direction: column;
  height: 100%;
  font-size: 6rem;
`;

const BackgroundPlay = styled.div`
  height: calc(100vh - 4rem);
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  text-align: center;
  color: white;
  transition: all 0.5s ease, opacity ${ANFORA_OUT_MS}ms ease-out;
  &[data-hidden="true"] { opacity: 0; }

  @media (max-height: 600px) {
    transition: all 0.5s ease;
    height: calc(100vh - 2rem);
  }

  @media (max-width: 768px) {
    width: 93%;
  }
  @media (max-width: 425px) {
    width: 96%;
  }
`;
const ContainerImg = styled.div`
  display: flex;
  flex-direction: column;
  height: ${(props) => (props.$error ? "83%" : "100%")};
  transition: all 0.5s ease;
`;

const PlaySorteoAnfora = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { salaId } = useContext(SiteContext);

  /* Obtener config según salaId */
  const config = tiempoAnimacion.find(x => x.salaId === Number(salaId))

  const winnerTime = config?.winnerAnimationTime ?? 14000;
  const countdownMs = config?.countdownTime ?? 0;
  const hasCountdown = config?.hasCountdown ?? false;

  /* Determinar si habrá conteo regresivo (y volver a decidirlo si cambia la config) */
  const withCountdown = hasCountdown && countdownMs > 0;
  const [showCountdown, setShowCountdown] = useState(withCountdown);
  const [showAnfora, setShowAnfora] = useState(!withCountdown);
  const [countdownConfig, setCountdownConfig] = useState(config);
  if (config !== countdownConfig) {
    setCountdownConfig(config);
    setShowCountdown(withCountdown);
    setShowAnfora(!withCountdown);
  }
  // El GIF de la sala 70 necesita una URL nueva en cada sorteo para empezar desde el primer cuadro, pero no en cada
  // render: su onLoad vuelve a renderizar y una URL distinta lo recargaba sin fin.
  const [gifSeed] = useState(() => Math.random());
  const [hardwareWebGL] = useState(hasHardwareWebGL);
  const [error, setError] = useState(null);
  const [count, setCount] = useState(0);
  const [outcome, setOutcome] = useState(null);
  const [pending, setPending] = useState(null);
  const [anforaGone, setAnforaGone] = useState(false);
  const [animTick, setAnimTick] = useState(0);
  const animStart = useRef(null);
  const [resultError, setResultError] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState(null);
  const confirmationPending = useRef(false);

  const beatRef = useRef(null);

  useEffect(() => {
    beatRef.current = new Audio(beepSound);
    beatRef.current.volume = 0.4; // suave para no asustar
  }, []);

  /* Nuestro countdown propio */
  const countdownSeconds = countdownMs > 0 ? Math.floor(countdownMs / 1000) : null;

  const countdown = useCustomCountdown(countdownSeconds, () => {
    setShowCountdown(false);
    setShowAnfora(true);
  });

  useEffect(() => {
    if (showCountdown && countdown !== null) {
      beatRef.current.currentTime = 0;
      beatRef.current.play();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countdown]);

  // El ganador se lee apenas se genera y se muestra, con la misma vista del sorteo 3D, cuando el cupón ya sale de la ánfora.
  const readResult = useCallback(() => {
    setResultError(false);
    axios
      .get(`${Apis.SORTEOS}/sorteos/${id}/winner`, { timeout: 20000 })
      .then(({ data }) => setPending({ winner: hasRaffleWinner(data) ? data : null }))
      .catch(() => setResultError(true));
  }, [id]);

  const onAnimationStart = useCallback(() => {
    animStart.current = performance.now();
    setAnimTick((value) => value + 1);
  }, []);

  useEffect(() => {
    if (!pending) return undefined;
    const reveal = () => { setOutcome(pending); setPending(null); };
    // Si el modelo nunca avisa que arrancó (falla de carga), no se espera para siempre.
    const safety = setTimeout(reveal, winnerTime + 8000);
    const wait = animStart.current === null ? null
      : Math.max(0, animStart.current + winnerTime - REVEAL_LEAD_MS - performance.now());
    const timer = wait === null ? null : setTimeout(reveal, wait);
    return () => { clearTimeout(safety); clearTimeout(timer); };
  }, [pending, animTick, winnerTime]);

  // La ánfora sigue visible mientras el resultado se funde por encima y recién después se retira.
  useEffect(() => {
    if (!outcome) return undefined;
    const timer = setTimeout(() => setAnforaGone(true), ANFORA_OUT_MS + 200);
    return () => clearTimeout(timer);
  }, [outcome]);

  const startWinnerGeneration = useCallback(() => {
    setError(null);
    setOutcome(null);
    setPending(null);
    setAnforaGone(false);
    animStart.current = null;
    setResultError(false);
    setConfirmError(null);
    setCount((prev) => prev + 1);

    axios
      .post(`${Apis.SORTEOS}/sorteos/${id}/generate-winner`)
      .then(readResult)
      .catch((err) => setError(err));
  }, [id, readResult]);

  const confirmWinner = async () => {
    if (!outcome?.winner || confirmationPending.current) return;
    if (!outcome.winner.sorteoWinnerId) { navigate("/sorteo-list"); return; }
    confirmationPending.current = true;
    setConfirming(true);
    setConfirmError(null);
    try {
      await axios.post(`${Apis.SORTEOS}/sorteo-winner/${outcome.winner.sorteoWinnerId}/confirm`);
      navigate("/sorteo-list");
    } catch {
      setConfirmError("No se pudo confirmar al ganador. Intenta confirmar nuevamente.");
    } finally {
      confirmationPending.current = false;
      setConfirming(false);
    }
  };

  /** Generar ganador al mostrar onfora */
  useEffect(() => {
    if (!showAnfora) return;

    // Arranca el sorteo (pedido al servidor) al aparecer el ánfora; los setState sincrónicos son el reset de esa
    // misma acción, que también usan los botones de reintentar.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    startWinnerGeneration();
  }, [showAnfora, startWinnerGeneration]);
  const getAnforaAnimation = () => {
    if (Number(salaId) === 70) {
      return (
        <img
          src={`${anforaSolbet}?a=${gifSeed}-${count}`}
          alt={"anfora"}
          onLoad={onAnimationStart}
          style={{ paddingTop: "2rem", width: "45vw" }}
        />
      );
    } else {
      // Sin WebGL con aceleración gráfica (ver util/webgl) el ánfora se dibuja en 2D con la misma secuencia y el sorteo
      // sigue igual. El ErrorBoundary cubre un <Canvas> que igual no logre crear el contexto, que antes tiraba toda la
      // página.
      const fallback = <AnforaFallback durationMs={winnerTime} error={error} onStart={onAnimationStart} />;
      if (!hardwareWebGL) return fallback;
      return (
        <ErrorBoundary fallback={fallback}>
          <Canvas camera={{ fov: 50 }} gl={WEBGL_HARDWARE_ONLY}>
            <Suspense fallback={null}>
              <Stage intensity={1} environment="studio">
                <AnforaAnimate tiempo={winnerTime / 1000} error={error} onStart={onAnimationStart} />
              </Stage>
            </Suspense>
          </Canvas>
        </ErrorBoundary>
      );
    }
  };

  return (
    <ContainerBackgroundWrapper showBackgroundCard={false} revealBackground={Boolean(outcome?.winner)}>
      <RaffleStage>
      <CountDownContainer $show={showCountdown}>
        <AnimatePresence>
          {countdown !== null && (
            <motion.h1
              key={countdown}
              animate={{ scale: [0, 3, 0], opacity: [0, 1, 0], position: "absolute" }}
              initial={{ opacity: 0 }}
              transition={{ ease: "easeOut", duration: 2 }}
            >
              {countdown}
            </motion.h1>
          )}
        </AnimatePresence>
      </CountDownContainer>
      {showAnfora && !anforaGone && !resultError && (
        <BackgroundPlay data-hidden={Boolean(outcome)}>
          <ContainerImg $error={error}>
            <GoldHeading>BUSCANDO AL GANADOR</GoldHeading>
            <div key={count} style={{ height: "inherit" }}>
              {getAnforaAnimation()}
            </div>
          </ContainerImg>
          {error && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                height: "17%",
                alignItems: "center",
              }}
            >
              <h4>¡Ups! tuvimos un problema al generar el ganador</h4>
              <Button onClick={startWinnerGeneration}>Reintentar</Button>
            </div>
          )}
        </BackgroundPlay>
      )}
      {resultError && (
        <BackgroundPlay>
          <h4>El sorteo se realizó, pero no pudimos leer el resultado.</h4>
          <Button onClick={readResult}>Consultar resultado</Button>
          <Button variant="outline" onClick={() => navigate("/sorteo-list")}>Volver a sorteos</Button>
        </BackgroundPlay>
      )}
      {outcome?.winner && (
        <ResultStage>
          <RaffleWinnerFallback winner={outcome.winner} />
          <RaffleWinnerOverlay winner={outcome.winner} salaId={salaId} onRestart={startWinnerGeneration} showActions headingDelayMs={1000}
            onConfirm={confirmWinner} confirming={confirming} error={confirmError} />
        </ResultStage>
      )}
      {outcome && !outcome.winner && (
        <ResultStage>
          <RaffleEmptyContent onRestart={startWinnerGeneration} onBack={() => navigate("/sorteo-list")} />
        </ResultStage>
      )}
      </RaffleStage>
    </ContainerBackgroundWrapper>
  );
};

export default PlaySorteoAnfora;


